import React, { useEffect, useState } from 'react';
import { GAME_STATIC_BASE, resolveGameBundle } from '../data/games';

const STYLE_ID = 'libby-game-styles';
let moduleLoadPromise = null;

function loadStylesheet(href) {
  const existing = document.getElementById(STYLE_ID);
  if (existing) {
    if (existing.href.endsWith(href)) return Promise.resolve();
    existing.remove();
  }
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.id = STYLE_ID;
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error('game-css'));
    document.head.append(link);
  });
}

function loadGameModule(src) {
  if (!moduleLoadPromise) {
    moduleLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('game-js'));
      document.head.append(script);
    });
  }
  return moduleLoadPromise;
}

function teardownGameMount() {
  document.querySelector('.hunt-dialog')?.close?.();
  document.querySelector('.hunt-dialog')?.remove();
  document.querySelector('.hunt-page')?.remove();
  document.getElementById('save-warning')?.remove();
  const app = document.querySelector('.libby-game-mount #app');
  if (app) {
    app.replaceChildren();
    app.classList.remove('app--hunt-page', 'app--site-embed');
  }
}

/**
 * Mounts the Vite-built Libby game bundle (no iframe).
 * @param {{ startMode?: 'detective' | '', className?: string }} props
 */
export default function LibbyGameMount({ startMode = '', className = '' }) {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setStatus('loading');
      try {
        const { js, css } = await resolveGameBundle();
        await loadStylesheet(css);
        if (cancelled) return;
        await loadGameModule(js);
        if (cancelled) return;
        window.__libbyGameBoot?.();
        if (!cancelled) setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      teardownGameMount();
    };
  }, [startMode]);

  const assetBase = GAME_STATIC_BASE.endsWith('/')
    ? GAME_STATIC_BASE
    : `${GAME_STATIC_BASE}/`;

  return (
    <div
      className={['libby-game-mount', 'libby-game-host', className].filter(Boolean).join(' ')}
      data-asset-base={assetBase}
      data-start-mode={startMode || undefined}
      data-hunt-page={startMode === 'detective' ? 'true' : undefined}
      data-site-embed="true"
    >
      <div id="libby-game-root">
        <div id="app" />
      </div>
      {status === 'loading' && (
        <p className="libby-game-mount__loading games-muted" role="status">
          Loading game…
        </p>
      )}
      {status === 'error' && (
        <p className="libby-game-mount__error games-muted" role="alert">
          We couldn&apos;t load the game. Try refreshing, or{' '}
          <a href={`${GAME_STATIC_BASE}/index.html`}>open the standalone version</a>.
        </p>
      )}
    </div>
  );
}
